<template>
  <div class="s-breadcrumb">
    <component
      :is="item.component"
      v-for="(item, idx) in computedItems"
      v-bind="item.props"
      :key="item.key"
      :to="item.to"
      class="s-breadcrumb__item text-h2"
      :class="{
        'text-vio-lighten-2': idx < items.length - 1,
        'text-tu-lighten-1': idx === items.length - 1,
      }"
      @click="selectItem(item)"
    >
      <span :class="{ 'text-break': item.canBreak === true }">{{ item.label }}</span>
      <v-icon v-if="idx < items.length - 1" :size="16">far fa-chevron-right</v-icon>
    </component>
  </div>
</template>

<script setup lang="ts">
import { RouteLocationRaw } from "vue-router"
import { computed } from "vue"

type BreadcrumbValue = number | string | null | undefined

export type Item = {
  label: string
  to?: RouteLocationRaw
  canBreak?: boolean
  value?: BreadcrumbValue
}
const props = defineProps<{
  items: Item[]
}>()
const modelValue = defineModel<BreadcrumbValue>()

type ComputedItem = Item & {
  component: "button" | "div" | "router-link"
  hasValue: boolean
  key: string
  props: Record<string, unknown>
}

const computedItems = computed<ComputedItem[]>(() => {
  return props.items.map((item) => {
    const hasValue = item.value !== undefined
    const key = getItemKey(item, hasValue)
    if (item.to) {
      return {
        ...item,
        component: "router-link",
        hasValue,
        key,
        props: {
          to: item.to,
        },
      }
    }

    if (hasValue && item.value !== modelValue.value) {
      return {
        ...item,
        component: "button",
        hasValue,
        key,
        props: {
          type: "button",
        },
      }
    }

    return {
      ...item,
      component: "div",
      hasValue,
      key,
      props: {},
    }
  })
})

function getItemKey(item: Item, hasValue: boolean): string {
  if (hasValue) {
    return `value:${typeof item.value}:${String(item.value)}`
  }

  if (item.to) {
    return `to:${typeof item.to === "string" ? item.to : JSON.stringify(item.to)}:${item.label}`
  }

  return `label:${item.label}`
}

function selectItem(item: ComputedItem): void {
  if (item.component !== "button" || !item.hasValue) {
    return
  }

  modelValue.value = item.value
}
</script>

<style lang="scss" scoped>
.s-breadcrumb {
  display: flex;
  padding: 8px;
  gap: 8px;
}

.s-breadcrumb__item {
  display: flex;
  gap: 8px;
  align-items: center;
  text-decoration: none;

  @at-root a#{&}:hover {
    & > span {
      color: $color-tu2 !important;
    }
  }

  @at-root button#{&}:hover {
    & > span {
      color: $color-tu2 !important;
    }
  }
}

button.s-breadcrumb__item {
  background: transparent;
  border: 0;
  cursor: pointer;
  padding: 0;
}
</style>
